import { FormikProps, useFormikContext } from 'formik'
import { CombinedFormPaymentSchedule } from '../PaymentSchedule/CombinedFormPaymentSchedule'
import { CombinedFormState } from '../../../../modules/combinedForm/reducer'
import { BoxV4, TextInputV4 } from '@labrav/react-components'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { useStyles } from './CombinedPricing.styles'
import { Typography } from '@mui/material'
import { useEffect } from 'react'
import { get } from 'lodash'
import { HelpDrawer } from '@labrav/common'
import { ScheduleType } from '../../../../modules/privateOffer/reducer'
import { awsMaxScheduledInstallments } from '../../../../common/utils/constants'

const calculateMarkup = (cppoTcv: number, resellerTcv: number) => {
  if (cppoTcv > 0 && resellerTcv > 0) {
    const value = ((cppoTcv - resellerTcv) / resellerTcv) * 100
    return Number.isInteger(value) ? value : parseFloat(value.toFixed(2))
  }
  return 0
}

export type CombinedPricingProps = {
  disabled: boolean
}

export default function CombinedPricing({ disabled }: CombinedPricingProps) {
  const { values, errors, touched, handleBlur, setFieldValue } =
    useFormikContext() as FormikProps<CombinedFormState>
  const formikProps = useFormikContext()
  const { classes } = useStyles()

  useEffect(() => {
    const markup = calculateMarkup(
      Number(values.cppoData.tcv),
      Number(values.resellerAgreementData.tcv)
    )
    setFieldValue('cppoData.adjustmentPercentage', markup)
  }, [values.cppoData.tcv, values.resellerAgreementData.tcv])

  return (
    <BoxV4
      heading={'Pricing'}
      headingIcon={
        <FontAwesomeIcon
          icon={faListCheck}
          style={{ width: '18px', height: '14px' }}
        />
      }
      secondaryActions={
        <HelpDrawer
          sectionName="combinedFormPricing"
          headerTitle="Help & FAQs"
        />
      }
    >
      <div className={classes.pricingContainer}>
        <div>
          <Typography
            className={classes.sectionHeading}
            style={{ marginBottom: '16px' }}
          >
            Total contract value
          </Typography>
          <div className={classes.pricingInputsContainer}>
            <div className={classes.pricingInput}>
              <TextInputV4
                keyName="resellerAgreementData.tcv"
                isReadOnly={disabled}
                type="number"
                showInsideInfoIcon={true}
                showInsideInfoIconTooltip={{
                  title: '',
                  content:
                    'Enter the TCV offered to the ISV after applying your discount.',
                }}
                isRequired
                value={values.resellerAgreementData.tcv || ''}
                updatedValue={values.resellerAgreementData.tcv || ''}
                placeHolder="Enter discounted TCV (to ISV)"
                title="Entered discounted TCV (to ISV)"
                onChange={e => {
                  setFieldValue('resellerAgreementData.tcv', e.target.value)
                  setFieldValue('resellerAgreementData.installmentInfo', [])
                  setFieldValue('cppoData.installmentInfo', [])
                  setFieldValue(
                    'cppoData.flexiblePaymentType',
                    ScheduleType.CUSTOM
                  )
                }}
                isError={
                  !!(
                    get(touched, 'resellerAgreementData.tcv', false) &&
                    get(errors, 'resellerAgreementData.tcv', false)
                  )
                }
                errorText={
                  (!!get(touched, 'resellerAgreementData.tcv', false) &&
                    get(errors, 'resellerAgreementData.tcv', '')) ||
                  ''
                }
                onBlur={handleBlur}
                keyToPrevent={['+', '-', 'e']}
              />
            </div>
            {!disabled && (
              <div className={classes.pricingInput}>
                <TextInputV4
                  keyName="cppoData.tcv"
                  isReadOnly={disabled}
                  showInsideInfoIcon={true}
                  type="number"
                  showInsideInfoIconTooltip={{
                    title: '',
                    content:
                      'Enter the TCV charged to the buyer after adding your markup.',
                  }}
                  isRequired
                  value={values.cppoData.tcv || ''}
                  updatedValue={values.cppoData.tcv || ''}
                  placeHolder="Enter marked-up TCV (from Buyer)"
                  title="Enter marked-up TCV (from Buyer)"
                  onChange={e => {
                    setFieldValue('cppoData.tcv', e.target.value)
                    setFieldValue('resellerAgreementData.installmentInfo', [])
                    setFieldValue('cppoData.installmentInfo', [])
                    setFieldValue(
                      'cppoData.flexiblePaymentType',
                      ScheduleType.CUSTOM
                    )
                  }}
                  isError={
                    !!(
                      get(touched, 'cppoData.tcv', false) &&
                      get(errors, 'cppoData.tcv', false)
                    )
                  }
                  errorText={
                    (!!get(touched, 'cppoData.tcv', false) &&
                      get(errors, 'cppoData.tcv', ' ')) ||
                    ' '
                  }
                  onBlur={handleBlur}
                  keyToPrevent={['+', '-', 'e']}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={classes.subContainer}>
        {!disabled && (
          <div>
            <Typography className={classes.sectionHeading}>
              Calculated markup percentage:{' '}
              {values.cppoData.adjustmentPercentage}%
            </Typography>
          </div>
        )}
        <CombinedFormPaymentSchedule
          formik={
            formikProps as unknown as FormikProps<Record<string, unknown>>
          }
          addButtonName={'Add more installments'}
          buttonCounterName={'installments'}
          shouldShowTotal={false}
          installmentsLimit={awsMaxScheduledInstallments}
          sectionDescription={''}
          tcvKey="adjustmentPercentageInputValue"
          fixTcvView={true}
          tcv={values.resellerAgreementData.tcv || 0}
          buyerTcv={values.cppoData.tcv || 0}
          handleChangeInstallmentsAmount={() => {}}
          disabled={disabled}
        />
      </div>
    </BoxV4>
  )
}
